import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { logger } from '../../utils/logger';

export interface ClientInteraction {
  id?: string;
  type: 'login' | 'profile_update' | 'document_upload' | 'document_delete' | 'chat_session' | 'growth_engine' | 'admin_action' | 'link_added' | 'link_opened' | 'link_edited' | 'link_validation_failed';
  timestamp: Timestamp;
  userId: string;
  userRole: 'client' | 'admin';
  userEmail: string;
  action: string;
  details: string;
  metadata?: Record<string, any>;
}

export const clientInteractionsService = {
  /**
   * Log interaction to client's subcollection
   * Following established pattern: clients/{clientId}/interactions/
   */
  async logInteraction(
    clientId: string, 
    interaction: Omit<ClientInteraction, 'id' | 'timestamp'>
  ): Promise<string> {
    try {
      const interactionsRef = collection(db, 'clients', clientId, 'interactions');
      const docRef = await addDoc(interactionsRef, {
        ...interaction,
        timestamp: serverTimestamp()
      });
      
      logger.debug(`✅ Logged interaction: ${interaction.action} for client ${clientId}`);
      return docRef.id;
    } catch (error) {
      logger.error('❌ Failed to log client interaction:', error);
      throw error;
    }
  },

  /**
   * Get client interaction history - FAST subcollection query
   * No global filtering needed - direct client-specific access
   */
  async getClientHistory(clientId: string, limitCount: number = 50): Promise<ClientInteraction[]> {
    try {
      const interactionsRef = collection(db, 'clients', clientId, 'interactions');
      const q = query(
        interactionsRef,
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ClientInteraction));
      
    } catch (error) {
      logger.error('❌ Failed to get client history:', error);
      return [];
    }
  },

  /**
   * Get client statistics from interactions
   * Fast aggregation of client-specific data
   */
  async getClientStats(clientId: string) {
    try {
      const interactions = await this.getClientHistory(clientId, 100);
      
      return {
        totalInteractions: interactions.length,
        lastActive: interactions[0]?.timestamp?.toDate?.() || null,
        profileUpdates: interactions.filter(i => i.type === 'profile_update').length,
        documentUploads: interactions.filter(i => i.type === 'document_upload').length,
        chatSessions: interactions.filter(i => i.type === 'chat_session').length,
        growthEngineActions: interactions.filter(i => i.type === 'growth_engine').length,
        adminActions: interactions.filter(i => i.type === 'admin_action').length,
        loginCount: interactions.filter(i => i.type === 'login').length
      };
    } catch (error) {
      logger.error('❌ Failed to get client stats:', error);
      return {
        totalInteractions: 0,
        lastActive: null,
        profileUpdates: 0,
        documentUploads: 0,
        chatSessions: 0,
        growthEngineActions: 0,
        adminActions: 0,
        loginCount: 0
      };
    }
  }
};
